// frontend/src/components/navigation/Login.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { loginUser } from "../../utils/authService";
import { updateSocketToken } from "../../utils/socket";
import { toast } from "../../utils/toast";
import { apiPublicGet } from "../../utils/api";
import { Eye, EyeOff, AlertTriangle, Check, X } from "lucide-react";
import logo from "../../assets/gca.png";
import { SUPPORT_EMAIL } from "../../utils/supportContacts";
import RegisterForm from "./RegisterForm.jsx";
import "./login.css";

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [maintAlert, setMaintAlert] = useState(null);
  // One registration UI, not two — /register renders this same component
  // (see App.jsx) rather than a separate, differently-styled page. Clicking
  // "Request Client Account" from here swaps the content in place instead
  // of a React Router navigation, so the brand panel/theme stay put (no
  // remount/flash); the address bar is kept in sync with a direct
  // history.replaceState alongside it.
  const [mode, setMode] = useState(() => (location.pathname === "/register" ? "register" : "login")); // "login" | "register"

  // Defensive resync for a *real* navigation to /register (e.g. a <Link
  // to="/register"> elsewhere in the app) — covers the case where React
  // Router preserves this component instance across that route change
  // instead of remounting it, in which case the useState initializer above
  // wouldn't re-run on its own. history.replaceState calls from
  // switchToRegister/switchToLogin below don't touch React Router's own
  // location, so they never trigger this.
  useEffect(() => {
    setMode(location.pathname === "/register" ? "register" : "login");
  }, [location.pathname]);

  const switchToRegister = () => {
    setMode("register");
    window.history.replaceState(null, "", "/register");
  };

  const switchToLogin = () => {
    setMode("login");
    window.history.replaceState(null, "", "/login");
  };

  useEffect(() => {
    let isMounted = true;
    const fetchMaintenance = async () => {
      try {
        const res = await apiPublicGet("/api/system-config/public");
        if (res.ok) {
          const data = await res.json();
          if (data.success && isMounted) {
            const config = {
              maintenanceStart: data.maintenanceStart ? new Date(data.maintenanceStart) : null,
              maintenanceEnd: data.maintenanceEnd ? new Date(data.maintenanceEnd) : null,
              maintenanceEnabled: !!data.maintenanceEnabled,
            };

            if (config.maintenanceEnabled && config.maintenanceStart && config.maintenanceEnd) {
              const now = Date.now();
              const start = config.maintenanceStart.getTime();
              const end = config.maintenanceEnd.getTime();

              if (now >= start && now < end) {
                setMaintAlert({
                  type: "active",
                  message: `System under maintenance until ${config.maintenanceEnd.toLocaleString("en-IN")}. Some services may be unavailable.`,
                });
              } else if (now < start && start - now < 24 * 60 * 60 * 1000) {
                setMaintAlert({
                  type: "upcoming",
                  message: `Scheduled maintenance is upcoming on ${config.maintenanceStart.toLocaleString("en-IN")}.`,
                });
              }
            }
          }
        }
      } catch (err) {
        console.error("Failed to load maintenance alert in Login", err);
      }
    };
    void fetchMaintenance();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const res = await loginUser(email, password);

    if (res.token) {
      const role = sessionStorage.getItem("role");
      updateSocketToken(res.token);

      const query = new URLSearchParams(location.search);
      const returnToRaw = String(query.get("returnTo") || "").trim();
      const safeReturnTo = returnToRaw.startsWith("/") && !returnToRaw.startsWith("//")
        ? returnToRaw
        : "";

      // Self-service roles (admin/client) must update a weak password before
      // continuing; other roles can't self-service, so just warn and proceed.
      const selfServiceRoles = new Set(["admin", "client"]);
      if (res.requiresPasswordReset && selfServiceRoles.has(role)) {
        toast.error("Your password doesn't meet our security policy. Please set a stronger one to continue.");
        navigate("/update-password", { state: { returnTo: safeReturnTo } });
        return;
      }

      toast.success("Welcome back! Authentication successful.");
      if (res.requiresPasswordReset) {
        toast.error("Your password doesn't meet our security policy. Ask your tenant admin to reset it.");
      }

      if (safeReturnTo) {
        navigate(safeReturnTo);
      } else if (role === "admin") {
        navigate("/admin/dashboard");
      } else if (role === "client") {
        // The seeded "download@<domain>" login is role:"client" too — send it to the
        // download page instead of the merged dashboard hub. Check both displayRole
        // and the email prefix since accounts seeded before displayRole existed
        // never got it backfilled.
        const displayRole = sessionStorage.getItem("displayRole") || "";
        const isDownloadOnlyAccount = displayRole === "Download Account" || email.trim().toLowerCase().startsWith("download@");
        // Everyone else lands on the merged dashboard hub by default now.
        navigate(isDownloadOnlyAccount ? "/download" : "/dashboard");
      } else if (role === "analytics_viewer" || role === "ceo") {
        navigate("/dashboard");
      } else if (role === "cfo") {
        navigate("/dashboard");
      } else if (role === "employee") {
        navigate("/dashboard");
      } else if (role === "manager") {
        navigate("/dashboard");
      } else {
        navigate("/role-accounts");
      }
    } else {
      toast.error(res.message || "Authentication failed. Please check your credentials.");
    }
  };

  return (
    <div className="login-container">
      <Link to="/" className="login-back-to-app">
        <X size={14} /> Back to app
      </Link>
      <div className="login-brand-panel">
        <div className="login-brand-mark">
          <img src={logo} alt="GCA Logo" className="login-brand-logo" />
          <span>Global Cyber Associates</span>
        </div>
        <h1 className="login-brand-tagline">Enterprise endpoint monitoring, unified.</h1>
        <ul className="login-brand-features">
          <li><Check size={13} /> Real-time screen &amp; activity monitoring</li>
          <li><Check size={13} /> USB, file, and URL threat tracking</li>
          <li><Check size={13} /> Productivity analytics across every team</li>
        </ul>
      </div>
      <div className="login-form-side">
      <div className="login-card">
        {mode === "register" ? (
          <RegisterForm onSwitchToLogin={switchToLogin} />
        ) : (
          <>
            <img src={logo} alt="GCA Logo" className="login-logo" />

            <h2 className="login-title">Welcome back</h2>
            <p className="login-subtitle">Sign in to continue to VisuN AI</p>

            {/* Maintenance Alert Banner */}
            {maintAlert && (
              <div className={`login-maint-alert login-maint-alert--${maintAlert.type}`}>
                <AlertTriangle size={15} />
                <span>{maintAlert.message}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  required
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    required
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button type="submit" className="login-btn login-submit-btn">
                Login
              </button>

              <p className="login-forgot-row">
                Forgot password?{" "}
                <a
                  className="login-forgot-link"
                  href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Password reset request")}`}
                >
                  Reset it
                </a>
              </p>

              <p className="registration-prompt">
                New to VisuN AI?{" "}
                <button type="button" className="register-link-btn" onClick={switchToRegister}>
                  Request Client Account
                </button>
              </p>
            </form>
          </>
        )}
      </div>
      <div className="login-footer">© {new Date().getFullYear()} Global Cyber Associates</div>
      </div>
    </div>
  );
}

export default Login;
